import moment from 'moment-timezone';

import { DateOptions } from './types';

export const getDateRangeToToday = (rangeInDays = 30) => {
  const today = new Date();
  const rangeInMilliseconds = rangeInDays * 24 * 60 * 60 * 1000;
  const startDateTime = new Date(today.getTime() - rangeInMilliseconds);

  return { today, startDateTime };
};

// Need to round the unix time to the nearest minute
// to avoid multiple api calls from a quickly changing unix timestamp
export const roundUpToNearestMinute = (unixTimestamp: number) => {
  // Create a Date object from the Unix timestamp (milliseconds)
  let date = new Date(unixTimestamp * 1000);

  // Round to nearest minute
  date.setMinutes(date.getMinutes() + 1);

  // Set seconds to zero
  date.setSeconds(0);
  date.setMilliseconds(0);

  // Return the Unix timestamp (seconds)
  return Math.floor(date.getTime() / 1000);
};

export const formatToUnixRounded = (timestamp: DateOptions) =>
  roundUpToNearestMinute(moment(timestamp)?.tz(moment.tz.guess()).unix());

export const currentDate = () => {
  return moment(new Date());
};

const DISPLAY_DATE_FORMAT = 'MMM D, YYYY';

// Returns today's date as "MMM D, YYYY" (e.g. "Jul 30, 2026").
export const formatToday = (): string => moment().format(DISPLAY_DATE_FORMAT);

// Formats a date string as "MMM D, YYYY"; returns "" for invalid input.
export const formatDisplayDate = (dateStr?: string | null): string => {
  const trimmed = dateStr?.trim();
  if (!trimmed) return '';

  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
    ? moment(trimmed, 'YYYY-MM-DD', true)
    : moment(trimmed);

  if (!parsed.isValid()) return '';
  return parsed.format(DISPLAY_DATE_FORMAT);
};

/**
 * Formats a date string to MM/DD/YYYY hh:mm z format in GMT timezone.
 *
 * @param dateStr - The input date string to be formatted
 * @returns The formatted date string in MM/DD/YYYY hh:mm z format
 *
 * @example
 * ```typescript
 * const result = formatDateTime('2023-12-25T15:30:00Z');
 * // Returns: "12/25/2023 03:30 GMT"
 * ```
 */
export const formatDateTime = (dateStr: string): string => {
  const formatted = moment.tz(dateStr, 'GMT').format('MM/DD/YYYY hh:mm z');
  return formatted;
};

/**
 * Formats a parseable date string for display in the user's local timezone.
 *
 * **Input:** Any string `moment` can parse (commonly ISO 8601, e.g. `2023-12-25T15:30:00Z`).
 * `null`, `undefined`, empty or whitespace-only string (after trim), the literal `'unknown'`
 * (ignoring surrounding whitespace), or any value that does not parse to a valid date all yield
 * the em dash placeholder.
 *
 * **Output:** `MMM D YYYY, h:mm:ss A` — month abbreviation, day, year, 12-hour time with
 * seconds and AM/PM (e.g. `Apr 7 2026, 3:45:02 PM`). Invalid or missing input returns `—`.
 *
 * @param dateStr - Date string to parse, or a sentinel empty/unknown value
 * @returns Formatted local date-time string, or `—` when there is nothing valid to show
 *
 * @example
 * ```typescript
 * formatDateTime12Hour('2026-04-07T14:30:00Z');
 * // e.g. "Apr 7 2026, 7:30:00 PM" (depends on local timezone)
 *
 * formatDateTime12Hour(null);
 * // "—"
 * ```
 */
export const formatDateTime12Hour = (
  dateStr: string | null | undefined,
): string => {
  const inputDate = dateStr?.trim();
  if (!inputDate || inputDate === 'unknown') return '—';
  const parsed = moment(inputDate);
  if (!parsed.isValid()) return '—';
  return parsed.format('MMM D YYYY, h:mm:ss A');
};

/**
 * Formats a Unix timestamp (seconds) or a date string into a human-readable
 * datetime string that includes the local GMT offset.
 *
 * **Accepted inputs**
 * - A `number` — interpreted as a Unix timestamp in **seconds** (not milliseconds).
 *
 * **Output format:** `MMMM D, YYYY h:mm A [GMT]Z`
 * e.g. `"November 19, 2025 10:00 AM GMT+05:30"`
 *
 * @param value - Unix timestamp (seconds), parseable date string, or empty/null sentinel
 * @returns Formatted datetime string, or `'—'` when the input is absent or invalid
 */
export const formatTimestampToDateTime = (
  value: string | number | null | undefined,
): string => {
  if (value == null || value === '') return '—';
  const numeric = typeof value === 'number' ? value : Number(value);
  const m = !Number.isNaN(numeric)
    ? moment.unix(numeric)
    : moment(value as string);
  if (!m.isValid()) return '—';
  return m.format('MMMM D, YYYY h:mm A [GMT]Z');
};

export const getDateRange = (range: string) => {
  let startDate: moment.Moment;
  let endDate: moment.Moment = moment().endOf('day');

  switch (range) {
    case 'Last 24 hours':
      startDate = moment().subtract(24, 'hours');
      endDate = moment(); // Current moment
      break;
    case 'Last 7 days':
      startDate = moment().subtract(7, 'days').startOf('day');
      break;
    default:
      throw new Error(
        "Invalid range. Supported ranges are 'Last 24 hours' or 'Last 7 days'.",
      );
  }

  return {
    startDate: startDate.format('YYYY-MM-DD HH:mm:ss'),
    endDate: endDate.format('YYYY-MM-DD HH:mm:ss'),
    formattedStartDate: startDate.format('MM/DD/YYYY'),
    formattedEndDate: endDate.format('MM/DD/YYYY'),
  };
};

export const formatDate = (
  startDate: moment.Moment | null,
  endDate?: moment.Moment | null,
) => {
  if (!startDate || !startDate.isValid()) return '';
  if (endDate && endDate.isValid()) {
    const timeDifference = endDate.diff(startDate, 'days');
    return timeDifference >= 1
      ? startDate.format('ddd MM/DD').toUpperCase()
      : startDate.format('h:mm A').replace(':00', 'AM');
  }
  return startDate.format('h:mm A').replace(':00', 'AM');
};

export const convertToSeconds = (timeStr: string): string => {
  const [value, unit] = timeStr.split(' ');
  const parsedUnit =
    unit.toLocaleLowerCase() as moment.unitOfTime.DurationConstructor;
  return String(moment.duration(parseInt(value), parsedUnit).asSeconds());
};

/**
 * Returns the day difference (endDate - startDate) between two date strings, parsed strictly
 * against the given formats. Negative when startDate is after endDate.
 * Returns 0 when either value is missing or invalid.
 */
export const getDayDifference = ({
  endDate,
  startDate,
  endFormat,
  startFormat,
}: {
  endDate?: string | null;
  startDate?: string | null;
  endFormat: string;
  startFormat: string;
}): number => {
  if (!endDate || !startDate) return 0;

  const endMoment = moment(endDate, endFormat, true);
  const startMoment = moment(startDate, startFormat, true);

  if (!endMoment.isValid() || !startMoment.isValid()) return 0;

  return Math.ceil(moment.duration(endMoment.diff(startMoment)).asDays());
};

export const formattedCreatedAt = (createdAt: string | null | undefined) =>
  createdAt
    ? new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(new Date(createdAt))
    : '';

/**
 * Formats a UTC date-time string into the user's local timezone and appends a GMT hour offset.
 *
 * Input can be a UTC date string or null/undefined. Empty, whitespace-only, and invalid values
 * return an empty string.
 *
 * Output format: dddd, MMM Do, YYYY at hh:mm A (GMT +/-H)
 * Example: Tuesday, Apr 7th, 2026 at 03:45 PM (GMT +5)
 *
 * @param utcString UTC date string to format, or null/undefined
 * @returns Formatted local date-time string with GMT hour offset, or empty string
 */

export const formatUtcToLocalWithGmt = (
  utcString: string | null | undefined,
): string => {
  const trimmedInput = utcString?.trim();
  if (!trimmedInput) return '';
  const isUnixTimestamp = /^\d+$/.test(trimmedInput);
  const numericValue = Number(trimmedInput);
  // Parse: unix seconds (10 digits), unix ms (13 digits), or ISO string — then localise
  const localMoment = (
    isUnixTimestamp
      ? trimmedInput.length === 10
        ? moment.unix(numericValue)
        : moment(numericValue)
      : moment.utc(trimmedInput)
  ).tz(moment.tz.guess());
  if (!localMoment.isValid()) return '';
  return `${localMoment.format(
    'dddd, MMM Do, YYYY [at] hh:mm A',
  )} (GMT ${localMoment.format('Z')})`;
};

// Formats the "added at" date for display, falling back to '-' if the date is invalid or missing.
export const formatAddedAt = (createdAt = '') => {
  return createdAt ? moment.utc(createdAt).local().format('MMM D, YYYY') : '-';
};
